import React, { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import useGroupItemContext from "../../../hooks/useGroupItemContext";

import { Dropdown, Menu, Table } from "antd";
import { Container as MenuContainer } from "../../../components/utils/table/menu/styles";
import MenuIcon from "../../../components/utils/table/icons/menu";
import TagStatus from "../../../components/utils/tagStatus";
import { getColumnSearchProps } from "../../../utils/tableUtils";

import { activate, inactivate } from "../../../services/groupItemService";
import { notification } from "antd";

const GroupItemTable = () => {
  const {
    translation,
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const { dataList, isLoadingDataList, openDrawer, fetchData } =
    useGroupItemContext();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = useCallback((selectedKeys, confirm) => {
    confirm();
  }, []);

  const handleReset = useCallback((clearFilters) => {
    clearFilters();
  }, []);

  const handleEdit = useCallback((record) => openDrawer(record), [openDrawer]);

  const handleInactivate = useCallback(
    async (id) => {
      try {
        await inactivate({ groupId, farmId, id });
        notification.success({
          message: translation.groupItem.table.inactivateSuccess,
        });
        fetchData();
      } catch (error) {
        notification.error({
          message: translation.groupItem.table.inactivateError,
        });
      }
    },
    [groupId, farmId, fetchData, translation],
  );

  const handleActivate = useCallback(
    async (id) => {
      try {
        await activate({ groupId, farmId, id });
        notification.success({
          message: translation.groupItem.table.activateSuccess,
        });
        fetchData();
      } catch (error) {
        notification.error({
          message: translation.groupItem.table.activateError,
        });
      }
    },
    [groupId, farmId, fetchData, translation],
  );

  function handleTableChange(pagination, filters, sorter) {
    let search = "";
    if (filters) {
      Object.entries(filters).forEach(([k, v]) => {
        if (v && v.length > 0) {
          if (k === "status") {
            const statusString = v.map((s) => `'${s}'`).join(",");
            search = `${search !== "" ? `${search};` : ""}${k} IN (${statusString})`;
          } else {
            search = `${search !== "" ? `${search};` : ""}upper(${k}) like upper('%25${v[0]}%25')`;
          }
        }
      });
    }
    const sort = sorter?.field || "id";
    const direction = sorter?.order === "descend" ? "DESC" : "ASC";
    fetchData({
      page: (pagination.current || 1) - 1,
      size: pagination.pageSize || 10,
      sort,
      direction,
      search: search || undefined,
    });
  }

  const menu = (id, record) => (
    <Menu>
      {record.status === "Active" && (
        <Menu.Item key="1" onClick={() => handleEdit(record)}>
          {translation.table.menu.edit}
        </Menu.Item>
      )}
      {record.status === "Active" && (
        <Menu.Item key="2" onClick={() => handleInactivate(id)}>
          {translation.table.menu.inactivate}
        </Menu.Item>
      )}
      {record.status === "Inactive" && (
        <Menu.Item key="3" onClick={() => handleActivate(id)}>
          {translation.table.menu.activate}
        </Menu.Item>
      )}
    </Menu>
  );

  const content = dataList?.content ?? [];
  const totalElements = dataList?.totalElements ?? 0;
  const currentPage = dataList != null ? (dataList.number ?? 0) + 1 : 1;
  const pageSize = dataList?.size ?? 10;

  return (
    <Table
      loading={isLoadingDataList}
      rowKey="id"
      size="small"
      dataSource={content}
      scroll={{ x: true }}
      pagination={{
        total: totalElements,
        current: currentPage,
        pageSize,
        hideOnSinglePage: totalElements <= 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "30", "100", "500", "1000", "2000"],
      }}
      onChange={handleTableChange}
    >
      <Table.Column
        title={translation.groupItem.table.columns.status}
        dataIndex="status"
        key="status"
        align="left"
        render={(status) =>
          status === "Active" ? (
            <TagStatus
              background="#C5F1CA"
              borderColor="#106518"
              color="#106518"
            >
              {translation.status.active}
            </TagStatus>
          ) : (
            <TagStatus
              background="#FBC7C7"
              borderColor="#D44C4C"
              color="#D44C4C"
            >
              {translation.status.inactive}
            </TagStatus>
          )
        }
      />
      <Table.Column
        title={translation.groupItem.table.columns.name}
        dataIndex="name"
        key="name"
      />
      <Table.Column
        title=""
        align="left"
        render={(text, record) => (
          <Dropdown
            overlay={menu(record.id, record)}
            trigger={["click"]}
            key={record.id}
          >
            <MenuContainer>
              <MenuIcon />
            </MenuContainer>
          </Dropdown>
        )}
      />
    </Table>
  );
};

export default GroupItemTable;
